"use client";

import { useEffect, useState } from "react";

export type TocItem = { id: string; label: string };

function useActiveSection(ids: string[]) {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);
  const key = ids.join("|");

  useEffect(() => {
    const list = key ? key.split("|") : [];
    if (list.length === 0) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      // The active section is the last heading that has scrolled past the sticky header.
      let current = list[0];
      for (const id of list) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 160) current = id;
      }
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      setActive(atBottom ? list[list.length - 1] : current);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [key]);

  return active;
}

function TocList({ items, active }: { items: TocItem[]; active: string | null }) {
  return (
    <ol className="flex flex-col gap-0.5">
      {items.map((item, index) => {
        const on = item.id === active;
        return (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              aria-current={on ? "location" : undefined}
              className={`flex min-h-9 items-baseline gap-3 rounded-lg border-l-2 py-1.5 pl-3 pr-2 text-sm leading-5 no-underline transition-colors ${
                on
                  ? "border-[#9e6df2] bg-[rgba(158,109,242,0.1)] text-white"
                  : "border-transparent text-slate-400 hover:bg-[rgba(158,109,242,0.06)] hover:text-white"
              }`}
            >
              <span className="mono w-5 shrink-0 text-xs text-slate-400" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span>{item.label}</span>
            </a>
          </li>
        );
      })}
    </ol>
  );
}

/** "On this page" list for the desktop rail, with scroll-spy highlighting. */
export function LegalToc({ items }: { items: TocItem[] }) {
  const active = useActiveSection(items.map((item) => item.id));
  if (items.length === 0) return null;
  return (
    <nav aria-label="On this page" className="stack g8">
      <span className="px-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">On this page</span>
      <TocList items={items} active={active} />
    </nav>
  );
}

/** Collapsible "On this page" list for small screens. */
export function LegalTocMobile({ items }: { items: TocItem[] }) {
  const active = useActiveSection(items.map((item) => item.id));
  if (items.length === 0) return null;
  return (
    <details className="group rounded-xl border border-slate-700 bg-slate-950">
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between px-4 text-sm font-semibold text-white [&::-webkit-details-marker]:hidden">
        On this page
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-[#a78bfa] transition-transform group-open:rotate-180"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </summary>
      <div className="border-t border-slate-800 p-2">
        <TocList items={items} active={active} />
      </div>
    </details>
  );
}
